import React, { useMemo, useRef, useState } from 'react';
import { Copy, RotateCcw, Trash2, Upload, ArrowUpToLine, ArrowDownToLine, Eye, EyeOff } from 'lucide-react';
import { cn, uploadSanitizedImage } from '../lib/utils';
import { toast } from '../lib/feedbackToast';
import { STICKER_PACKS, createImageDecoration, createStickerDecoration, getSticker, normalizeDecorations } from '../lib/stickerPacks';
import ProfileView from '../features/ProfileView';

export const DecorationLayer = ({ decorations, className, profileId }) => {
  const safeDecorations = normalizeDecorations(decorations);
  if (!safeDecorations.length) return null;

  return (
    <div className={cn('pointer-events-none absolute inset-0 z-30 overflow-visible', className)} aria-hidden="true">
      {safeDecorations.map((item) => {
        const sticker = getSticker(item.asset);
        const content = (
          <span
            key={item.id}
            className={cn('absolute select-none text-center font-black leading-none drop-shadow-[0_4px_5px_rgba(15,23,42,0.2)]', item.kind !== 'image' && sticker.kind === 'label' && 'rounded-full border-2 border-white px-2 py-1 text-[8px] tracking-[0.12em] shadow-md')}
            style={{
              left: `${item.x}%`, top: `${item.y}%`, width: `${item.width}%`,
              transform: `translate(-50%, -50%) rotate(${item.rotation}deg)`,
              color: sticker.kind === 'label' ? '#fff' : sticker.color,
              background: item.kind !== 'image' && sticker.kind === 'label' ? sticker.color : undefined,
              fontSize: item.kind !== 'image' && sticker.kind === 'label' ? undefined : 'clamp(1.5rem, 5vw, 3rem)',
              zIndex: item.zIndex, opacity: item.opacity,
            }}
          >
            {item.kind === 'image' ? <img src={item.src} alt="" className="h-auto w-full object-contain" /> : sticker.glyph}
          </span>
        );
        if (!item.actionUrl && !item.tooltip) {
          return React.cloneElement(content, { title: item.tooltip || undefined });
        }
        
        if (!item.actionUrl && item.tooltip) {
          // It has a tooltip but no URL. We make it interactive to show the tooltip on hover.
          return (
            <div
              key={item.id}
              className="pointer-events-auto absolute z-40"
              style={{ left: `${item.x}%`, top: `${item.y}%`, width: `${item.width}%`, transform: `translate(-50%, -50%) rotate(${item.rotation}deg)` }}
              title={item.tooltip}
              aria-label={item.tooltip}
            >
              <span className={cn('block select-none text-center font-black leading-none drop-shadow-[0_4px_5px_rgba(15,23,42,0.2)]', item.kind !== 'image' && sticker.kind === 'label' && 'rounded-full border-2 border-white px-2 py-1 text-[8px] tracking-[0.12em] shadow-md')}
                style={{ color: sticker.kind === 'label' ? '#fff' : sticker.color, background: item.kind !== 'image' && sticker.kind === 'label' ? sticker.color : undefined, fontSize: item.kind !== 'image' && sticker.kind === 'label' ? undefined : 'clamp(1.5rem, 5vw, 3rem)', opacity: item.opacity }}>
                {item.kind === 'image' ? <img src={item.src} alt="" className="h-auto w-full object-contain" /> : sticker.glyph}
              </span>
            </div>
          );
        }

        return (
          <a
            key={item.id}
            href={item.actionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pointer-events-auto absolute z-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-950"
            style={{ left: `${item.x}%`, top: `${item.y}%`, width: `${item.width}%`, transform: `translate(-50%, -50%) rotate(${item.rotation}deg)` }}
            title={item.tooltip || undefined}
            aria-label={item.tooltip || `${sticker.label} link`}
            onClick={() => {
              if (!profileId) return;
              fetch('/api/v1/track', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ profile_id: profileId, event_type: 'decoration_click', url: item.actionUrl, destination_platform: 'decoration' }),
              }).catch(() => {});
            }}
          >
            <span className={cn('block select-none text-center font-black leading-none drop-shadow-[0_4px_5px_rgba(15,23,42,0.2)]', item.kind !== 'image' && sticker.kind === 'label' && 'rounded-full border-2 border-white px-2 py-1 text-[8px] tracking-[0.12em] shadow-md')}
              style={{ color: sticker.kind === 'label' ? '#fff' : sticker.color, background: item.kind !== 'image' && sticker.kind === 'label' ? sticker.color : undefined, fontSize: item.kind !== 'image' && sticker.kind === 'label' ? undefined : 'clamp(1.5rem, 5vw, 3rem)', opacity: item.opacity }}>
              {item.kind === 'image' ? <img src={item.src} alt="" className="h-auto w-full object-contain" /> : sticker.glyph}
            </span>
          </a>
        );
      })}
    </div>
  );
};

const DecorationCanvas = ({ profile, links, onUpdate }) => {
  const canvasRef = useRef(null);
  const decorations = useMemo(() => normalizeDecorations(profile?.profile_decorations), [profile?.profile_decorations]);
  const [selectedId, setSelectedId] = useState(decorations[0]?.id || null);
  const [hideDecorations, setHideDecorations] = useState(false);
  const [history, setHistory] = useState([decorations]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const dragRef = useRef(null);
  const selected = decorations.find((item) => item.id === selectedId) || null;

  const save = (next) => {
    onUpdate({ profile_decorations: normalizeDecorations(next) });
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(next);
    if (newHistory.length > 50) newHistory.shift(); // keep last 50 states
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };
  const updateSelected = (changes) => save(decorations.map((item) => item.id === selectedId ? { ...item, ...changes } : item));

  // Keyboard controls
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          // Redo
          if (historyIndex < history.length - 1) {
            const nextIdx = historyIndex + 1;
            setHistoryIndex(nextIdx);
            onUpdate({ profile_decorations: normalizeDecorations(history[nextIdx]) });
          }
        } else {
          // Undo
          if (historyIndex > 0) {
            const nextIdx = historyIndex - 1;
            setHistoryIndex(nextIdx);
            onUpdate({ profile_decorations: normalizeDecorations(history[nextIdx]) });
          }
        }
        return;
      }

      if (!selected) return;
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return; // Don't interfere with inputs

      let dx = 0;
      let dy = 0;
      if (e.key === 'ArrowUp') dy = -1;
      if (e.key === 'ArrowDown') dy = 1;
      if (e.key === 'ArrowLeft') dx = -1;
      if (e.key === 'ArrowRight') dx = 1;

      if (dx !== 0 || dy !== 0) {
        e.preventDefault();
        if (e.shiftKey) {
          // Resize
          const newWidth = Math.max(8, Math.min(42, selected.width + (dx !== 0 ? dx : -dy)));
          updateSelected({ width: newWidth });
        } else if (e.altKey) {
          // Rotate
          let newRot = selected.rotation + (dx !== 0 ? dx * 5 : dy * 5);
          if (newRot > 180) newRot = -180 + (newRot - 180);
          if (newRot < -180) newRot = 180 - (-180 - newRot);
          updateSelected({ rotation: newRot });
        } else {
          // Move
          const speed = e.metaKey || e.ctrlKey ? 5 : 1;
          updateSelected({
            x: Math.max(0, Math.min(100, selected.x + dx * speed)),
            y: Math.max(0, Math.min(100, selected.y + dy * speed))
          });
        }
      }
      
      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (!['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
           e.preventDefault();
           const next = decorations.filter((item) => item.id !== selected.id); 
           setSelectedId(next[0]?.id || null); 
           save(next);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selected, decorations, updateSelected, save]);

  const beginDrag = (event, item) => {
    event.preventDefault();
    setSelectedId(item.id);
    dragRef.current = item.id;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveDrag = (event) => {
    if (!dragRef.current || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    let x = ((event.clientX - rect.left) / rect.width) * 100;
    let y = ((event.clientY - rect.top) / rect.height) * 100;
    
    // Snapping logic (within 3% threshold)
    if (Math.abs(x - 50) < 3) x = 50;
    if (Math.abs(y - 50) < 3) y = 50;
    
    save(decorations.map((item) => item.id === dragRef.current ? { ...item, x, y } : item));
  };

  const endDrag = () => { dragRef.current = null; };
  const add = (asset) => {
    if (decorations.length >= 12) return;
    const next = [...decorations, createStickerDecoration(asset, decorations.length)];
    setSelectedId(next.at(-1).id);
    save(next);
  };
  const uploadCustom = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || decorations.length >= 12) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 1_000_000) {
      toast.error('Use a PNG, JPG, or WebP under 1 MB.');
      return;
    }
    try {
      const loading = toast.loading('Preparing your decoration…');
      const url = await uploadSanitizedImage(file, 'decorations', `${profile.id}/decoration-${Date.now()}.webp`);
      const next = [...decorations, createImageDecoration(url, decorations.length)];
      setSelectedId(next.at(-1).id);
      save(next);
      toast.success('Custom decoration added', { id: loading });
    } catch (error) {
      toast.error(error?.message || 'Could not upload decoration.');
    }
  };

  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-4">
      <header className="flex items-start justify-between">
        <div>
          <h3 className="text-xl font-black">Decorate</h3>
          <p className="mt-1 text-sm text-slate-500">Add movable accents without changing the responsive structure of your profile.</p>
        </div>
        <button type="button" onClick={() => setHideDecorations(!hideDecorations)} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50">
          {hideDecorations ? <EyeOff size={14} /> : <Eye size={14} />} {hideDecorations ? 'Hidden' : 'Visible'}
        </button>
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="rounded-[2rem] border border-slate-200 bg-slate-100 p-4 sm:p-6">
          <div
            ref={canvasRef}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            className="dashboard-phone-frame relative mx-auto w-[340px] aspect-[9/14] bg-white rounded-[3rem] p-3 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.15)] border-[8px] border-slate-900"
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-2xl z-50 pointer-events-none" />
            <div className="dashboard-phone-screen w-full h-full rounded-[2.2rem] overflow-hidden bg-slate-50 relative">
              <div className="pointer-events-none absolute inset-0 w-full h-full">
                <ProfileView injectedProfile={profile} injectedLinks={links} isMockup={true} />
              </div>
              {!hideDecorations && (
              <>
                <DecorationLayer decorations={decorations} />
                {decorations.map((item) => {
                  const sticker = getSticker(item.asset);
              const isSelected = item.id === selectedId;
              return (
                <button
                  type="button"
                  key={item.id}
                  onPointerDown={(event) => beginDrag(event, item)}
                  className={cn('absolute z-40 cursor-grab touch-none active:cursor-grabbing', isSelected && 'rounded-xl outline outline-2 outline-slate-900 outline-offset-2')}
                  style={{ left: `${item.x}%`, top: `${item.y}%`, width: `${item.width}%`, transform: `translate(-50%, -50%) rotate(${item.rotation}deg)` }}
                  aria-label={`Move ${sticker.label} sticker`}
                >
                      <span className={cn('block select-none text-center font-black leading-none', item.kind !== 'image' && sticker.kind === 'label' && 'rounded-full border-2 border-white px-2 py-1 text-[8px] tracking-[0.12em] shadow-md')}
                    style={{ color: sticker.kind === 'label' ? '#fff' : sticker.color, background: item.kind !== 'image' && sticker.kind === 'label' ? sticker.color : undefined, fontSize: item.kind !== 'image' && sticker.kind === 'label' ? undefined : '2.25rem', opacity: item.opacity }}>
                    {item.kind === 'image' ? <img src={item.src} alt="" className="h-auto w-full object-contain" /> : sticker.glyph}
                  </span>
                </button>
              );
            })}
              </>
            )}
          </div>
          </div>
          <p className="mt-4 text-center text-xs font-medium text-slate-500">Drag an accent in the preview. Decorations are visual only and never intercept visitor taps.</p>
        </div>

        <aside className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Sticker packs</p>
            <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-3 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:border-slate-900 hover:text-slate-900">
              <Upload size={14} /> Add your artwork
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadCustom} className="hidden" disabled={decorations.length >= 12} />
            </label>
            <p className="mt-2 text-center text-[9px] font-bold text-slate-400">Use transparent PNGs or WebPs</p>
            {STICKER_PACKS.map((pack) => (
              <div key={pack.id} className="mt-4">
                <p className="mb-2 text-xs font-bold text-slate-600">{pack.name}</p>
                <div className="grid grid-cols-3 gap-2">
                  {pack.stickers.map((sticker) => (
                    <button key={sticker.id} type="button" onClick={() => add(sticker.id)} disabled={decorations.length >= 12}
                      className="flex min-h-14 flex-col items-center justify-center rounded-xl border border-slate-200 bg-white text-xs font-black transition hover:border-slate-900 disabled:cursor-not-allowed disabled:opacity-40">
                      <span style={{ color: sticker.color }}>{sticker.glyph}</span><span className="mt-1 text-[8px] uppercase text-slate-400">{sticker.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </section>

          {selected ? (
            <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between"><p className="text-sm font-black">Edit sticker</p><span className="text-[10px] font-bold text-slate-400">{getSticker(selected.asset).label}</span></div>
              {[['Size', 'width', 8, 42, '%'], ['Rotation', 'rotation', -180, 180, '°'], ['Opacity', 'opacity', 20, 100, '%']].map(([label, field, min, max, unit]) => {
                const rawValue = field === 'opacity' ? Math.round(selected.opacity * 100) : selected[field];
                return <label key={field} className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{label} <span className="float-right text-slate-600">{rawValue}{unit}</span>
                  <input className="mt-2 w-full accent-slate-900" type="range" min={min} max={max} value={rawValue} onChange={(event) => updateSelected({ [field]: field === 'opacity' ? Number(event.target.value) / 100 : Number(event.target.value) })} />
                </label>;
              })}
              <div className="grid grid-cols-5 gap-2">
                <button type="button" onClick={() => updateSelected({ rotation: selected.rotation - 15 })} className="rounded-xl border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 flex justify-center" aria-label="Rotate left"><RotateCcw size={15} /></button>
                <button type="button" onClick={() => updateSelected({ zIndex: Math.min(20, selected.zIndex + 1) })} className="rounded-xl border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 flex justify-center" aria-label="Bring forward"><ArrowUpToLine size={15} /></button>
                <button type="button" onClick={() => updateSelected({ zIndex: Math.max(1, selected.zIndex - 1) })} className="rounded-xl border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 flex justify-center" aria-label="Send backward"><ArrowDownToLine size={15} /></button>
                <button type="button" disabled={decorations.length >= 12} onClick={() => { const duplicate = { ...selected, ...createStickerDecoration(selected.asset, decorations.length), x: Math.min(94, selected.x + 7), y: Math.min(94, selected.y + 7), rotation: selected.rotation + 8 }; const next = [...decorations, duplicate]; setSelectedId(duplicate.id); save(next); }} className="rounded-xl border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 flex justify-center" aria-label="Duplicate sticker"><Copy size={15} /></button>
                <button type="button" onClick={() => { const next = decorations.filter((item) => item.id !== selected.id); setSelectedId(next[0]?.id || null); save(next); }} className="rounded-xl border border-rose-100 p-2 text-rose-500 hover:bg-rose-50 flex justify-center" aria-label="Delete sticker"><Trash2 size={15} /></button>
              </div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">Optional action
                <input type="url" value={selected.actionUrl || ''} onChange={(event) => updateSelected({ actionUrl: event.target.value })} placeholder="https://…" className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium normal-case tracking-normal outline-none focus:border-slate-900" />
              </label>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mt-3">Tooltip (Hover Text)
                <input type="text" value={selected.tooltip || ''} onChange={(event) => updateSelected({ tooltip: event.target.value })} placeholder="Sale ends soon!" maxLength={50} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium normal-case tracking-normal outline-none focus:border-slate-900" />
              </label>
              <p className="text-[10px] font-medium leading-relaxed text-slate-400">Leave blank for decoration only. Interactive stickers are shown as links and recorded as decoration clicks.</p>
              {(selected.actionUrl || selected.tooltip) && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 font-medium">
                  ⚠️ <strong>Interactive Sticker:</strong> Make sure this doesn't cover your main links! It will block taps on anything underneath it.
                </div>
              )}
            </section>
          ) : <p className="rounded-2xl bg-slate-50 p-4 text-xs font-medium text-slate-500">Select a sticker to adjust it. Up to 12 decorations keep public profiles fast and readable.</p>}
        </aside>
      </div>
    </div>
  );
};

export default DecorationCanvas;
